import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Koshi Guards — "Add 1 Display Stand for $1" upsell.
 *
 * Purely additive: does not modify or intercept any existing add-to-cart flow.
 * It listens for the theme's own CartLinesUpdateEvent (dispatched by
 * product-form-component after a successful add) and, if the shopper checked
 * the Display Stand checkbox on the product page, fires a second /cart/add.js
 * request for the Display Stand variant. It also powers a manual
 * "Add Display Stand" button inside the cart drawer.
 */

const DISPLAY_STAND_VARIANT_ID = 67628347851002;

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function addDisplayStand() {
  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: DISPLAY_STAND_VARIANT_ID, quantity: 1 }],
        sections: getCartSectionIds().join(','),
      }),
    });

    const data = await response.json();

    if (data.status) {
      console.warn('[display-stand-addon] Could not add Display Stand:', data.message);
      return;
    }

    // The /cart/add.js response (items[] form) does not include the cart's
    // currency code, which CartLinesUpdateEvent.createCartFromAjaxResponse()
    // needs in order to format money values — passing it the raw add response
    // throws "Currency code is required with currency style." and aborts
    // before the cart drawer re-renders, even though the item was already
    // added server-side. Fetch the full cart object (which does carry
    // currency) the same way product-form-component does, and build the
    // event from that instead.
    const cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((res) => res.json());

    // Let the theme's cart components know, using the same contract
    // product-form-component uses, so the cart drawer / cart page re-render
    // themselves from the returned section HTML.
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'display-stand-addon',
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${DISPLAY_STAND_VARIANT_ID}`, quantity: 1 }],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'display-stand-addon',
            sourceId: 'display-stand-addon',
            itemCount: cart.item_count,
            sections: data.sections,
            didError: false,
          },
        }),
      })
    );
  } catch (error) {
    console.warn('[display-stand-addon] Network error adding Display Stand:', error);
  }
}

// Auto-add when a shopper checked the product-page checkbox and then added a
// guard to their cart.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.context !== 'product' || event.action !== 'add') return;

  const checkbox = document.querySelector('[data-display-stand-addon]');
  if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return;

  addDisplayStand();
});

// Manual "Add Display Stand" button, used in the cart drawer upsell row.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-add-display-stand]') : null;
  if (!target || target.hasAttribute('disabled')) return;

  event.preventDefault();
  target.setAttribute('disabled', 'true');
  const originalText = target.textContent;
  target.textContent = '…';

  addDisplayStand().finally(() => {
    target.removeAttribute('disabled');
    target.textContent = originalText;
  });
});
